import React, { memo, useMemo, useState } from 'react';
import { Tooltip, Typography, Chip, MenuItem, Select, useTheme } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../DiagnosticRevisionTable.style';
import { diagnosticsClassColor } from '../../../../../../helpers/manageColors';
import { getClassChangeTooltipContent } from '../utils/utils';
import { OPACITIES_OPTIONS, MICROCALC_CLASSES } from '../constants/constants';
import { isMissedFinding } from '../../../../../../helpers/diagnostics/detectionOrigin';

const ClassCell = ({ nested, lesionAllInfo, handleClassChangeWrapper, smallScreen }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const [editingClassRowId, setEditingClassRowId] = useState(null);

  // Extract data from lesionAllInfo
  const module_type = lesionAllInfo?.module_type;
  const isClustered = lesionAllInfo?.clustered;
  const projection = Array.isArray(lesionAllInfo?.projection)
    ? lesionAllInfo.projection[0]
    : lesionAllInfo?.projection;
  const matched = lesionAllInfo?.matched_lesions || lesionAllInfo?.match;
  const currentClass = lesionAllInfo?.finding_class;
  const uniqueId = lesionAllInfo?.finding_id;
  const missingLesions = isMissedFinding(lesionAllInfo);

  const classOptions = module_type === 'microcalc' ? MICROCALC_CLASSES : OPACITIES_OPTIONS;

  // Extract matched lesion info
  const matchedLesions = lesionAllInfo?.matched_lesions;
  const ccFinding = matchedLesions?.find((f) => {
    const proj = Array.isArray(f.projection) ? f.projection[0] : f.projection;
    return proj && proj.includes('cc');
  });
  const mloFinding = matchedLesions?.find((f) => {
    const proj = Array.isArray(f.projection) ? f.projection[0] : f.projection;
    return proj && proj.includes('mlo');
  });
  const matchedLesionAllInfo = mloFinding || ccFinding;

  const severityChangeStart =
    lesionAllInfo?.severity_change_first || matchedLesionAllInfo?.severity_change_first;
  const severityChangeLatest =
    lesionAllInfo?.severity_change_latest || matchedLesionAllInfo?.severity_change_latest;

  const isEditing = useMemo(() => {
    return editingClassRowId === uniqueId;
  }, [editingClassRowId, uniqueId]);

  const formatBiradsLabel = (value) => {
    if (!value) return '';
    if (value === 'lesionKnown') {
      return smallScreen ? t('projections.lesionKnownShort') : t('projections.lesionKnown');
    } else if (value === 'vessel') {
      return smallScreen ? 'V' : t('projections.vessels');
    }
    const level = value?.slice(6, 7);
    return smallScreen ? level : t(`revision.${value}`);
  };

  const renderTooltipContent = (cls) =>
    getClassChangeTooltipContent(
      projection,
      matched,
      severityChangeStart,
      severityChangeLatest,
      cls,
      isClustered,
      t,
      lesionAllInfo,
      matchedLesionAllInfo,
      classes,
      theme,
    );

  const stopEvent = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };
  if (isEditing) {
    const validValue = classOptions.includes(currentClass) ? currentClass : classOptions[0];

    return (
      <Select
        key={`class-select-${uniqueId}`}
        variant='outlined'
        open
        value={validValue}
        onClose={() => setEditingClassRowId(null)}
        className={`${classes.selectNoBorder} row-class-change-select`}
        MenuProps={{
          PaperProps: {
            style: {
              zIndex: 9999,
            },
          },
        }}>
        {classOptions.map((option) => (
          <MenuItem
            key={option}
            value={option}
            className={classes.chip_container}
            onClick={() => handleClassChangeWrapper(lesionAllInfo, option)}
            onPointerDown={stopEvent}
            disabled={currentClass === option && !missingLesions}>
            <Typography component='span' className={classes.chip_texts}>
              {formatBiradsLabel(option)}
              {currentClass === option && missingLesions && '*'}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    );
  }

  //Nested mode (Chip)
  if (nested) {
    const nestedClass = lesionAllInfo?.class || lesionAllInfo?.birads || currentClass;
    const tooltipContent = renderTooltipContent(nestedClass);
    const tooltipTitle =
      typeof tooltipContent === 'string' ? (
        <Typography className={classes.tooltip_text}>{tooltipContent}</Typography>
      ) : (
        tooltipContent
      );

    return (
      <Tooltip arrow title={tooltipTitle}>
        <Chip
          avatar={<ArrowDropDownIcon style={{ margin: 0 }} />}
          clickable
          size='small'
          className={classes.class_chip}
          style={{
            background: missingLesions
              ? `${diagnosticsClassColor(nestedClass, theme)}30`
              : `${diagnosticsClassColor(nestedClass, theme)}80`,
          }}
          label={
            <div className={classes.chip_container}>
              <Typography className={classes.chip_texts}>{formatBiradsLabel(nestedClass)}</Typography>
            </div>
          }
          onClick={(e) => {
            stopEvent(e);
            requestAnimationFrame(() => {
              setEditingClassRowId(uniqueId);
            });
          }}
          onPointerDown={stopEvent}
          classes={{ labelSmall: classes.customChipLabelSmall }}
        />
      </Tooltip>
    );
  }

  //Main mode (Chip)
  const mainTooltipContent = renderTooltipContent(currentClass);
  const mainTooltipTitle =
    typeof mainTooltipContent === 'string' ? (
      <Typography className={classes.tooltip_text}>{mainTooltipContent}</Typography>
    ) : (
      mainTooltipContent
    );

  return (
    <Tooltip arrow title={mainTooltipTitle}>
      <Chip
        avatar={isClustered ? undefined : <ArrowDropDownIcon style={{ margin: 0 }} />}
        clickable={!isClustered}
        id='row-class-change'
        size='small'
        className={classes.class_chip}
        style={{
          background: missingLesions
            ? `${diagnosticsClassColor(currentClass, theme)}30`
            : `${diagnosticsClassColor(currentClass, theme)}80`,
        }}
        label={
          <div className={classes.chip_container}>
            {/* Class label */}
            <Typography className={classes.chip_texts}>
              {isClustered || missingLesions
                ? lesionAllInfo.birads_range
                  ? smallScreen
                    ? lesionAllInfo.birads_range
                    : `BI-RADS ${lesionAllInfo.birads_range}`
                  : formatBiradsLabel(currentClass)
                : formatBiradsLabel(currentClass)}
            </Typography>

            {/* Trend arrow */}
            <Typography className={classes.chip_texts} style={{ lineHeight: '11px' }}>
              {lesionAllInfo?.severity_change_latest
                ? lesionAllInfo.severity_change_latest > 0
                  ? '↑'
                  : lesionAllInfo.severity_change_latest < 0
                    ? '↓'
                    : '↔'
                : ''}
            </Typography>
          </div>
        }
        onClick={(e) => {
          stopEvent(e);
          if (!isClustered) {
            requestAnimationFrame(() => {
              setEditingClassRowId(uniqueId);
            });
          }
        }}
        onPointerDown={stopEvent}
        classes={{ labelSmall: classes.customChipLabelSmall }}
      />
    </Tooltip>
  );
};

export default memo(ClassCell);
